import { useCallback, useEffect, useState, type FormEvent } from 'react';
import { Link, useParams } from 'react-router-dom';

import { NeoMarkdown } from '../../components/NeoChat';
import { adminApi } from '../adminApi';
import { StatusBadge } from '../components/StatusBadge';
import { formatDate } from '../format';
import {
  apiErrorMessage,
  ciLabel,
  phaseLabel,
  type NeoChangeDetail,
  type NeoChangeStatus,
} from '../neoChanges';

// One Neo product change (ADR 0231): its live status from GitHub, the PR's
// files (the ones a reviewer should read closely first), and the actions.
// Merge deploys to production, so it asks for a fresh authenticator code and
// sends the head SHA shown here; the server refuses if the PR moved since.

const POLL_MS = 15_000;

function MergeForm({
  changeId,
  headSha,
  onDone,
}: {
  changeId: string;
  headSha: string;
  onDone: () => void;
}) {
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await adminApi(`/v1/admin/neo/changes/${changeId}/merge`, {
        method: 'POST',
        body: { code, expectedHeadSha: headSha },
      });
      onDone();
    } catch (err) {
      setError(apiErrorMessage(err, 'The merge did not go through.'));
      setCode('');
    } finally {
      setBusy(false);
    }
  }

  return (
    <form className="neo-change-merge" onSubmit={(e) => void submit(e)}>
      <p>
        Merging squashes this PR into main and <strong>deploys it to production</strong>. Enter the
        code from your authenticator to confirm.
      </p>
      <label htmlFor="neo-merge-code">Authenticator code</label>
      <input
        id="neo-merge-code"
        inputMode="numeric"
        autoComplete="one-time-code"
        pattern="\d{6}"
        maxLength={6}
        value={code}
        onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
        required
      />
      <button type="submit" className="admin-button-danger" disabled={busy || code.length !== 6}>
        {busy ? 'Merging…' : 'Merge and deploy'}
      </button>
      {error !== null && <p className="admin-inline-error">{error}</p>}
    </form>
  );
}

function Actions({
  changeId,
  status,
  reload,
}: {
  changeId: string;
  status: NeoChangeStatus;
  reload: () => void;
}) {
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [revision, setRevision] = useState('');
  const [confirmClose, setConfirmClose] = useState(false);
  const [merging, setMerging] = useState(false);
  const open = ['running', 'failed', 'pr_open'].includes(status.phase);

  async function act(path: string, body?: unknown) {
    setBusy(path);
    setError(null);
    try {
      await adminApi(`/v1/admin/neo/changes/${changeId}/${path}`, { method: 'POST', body });
      setRevision('');
      setConfirmClose(false);
      reload();
    } catch (err) {
      setError(apiErrorMessage(err, 'That did not work.'));
    } finally {
      setBusy(null);
    }
  }

  if (!open) return null;
  return (
    <div className="neo-change-actions">
      <h2>Actions</h2>
      {status.pr !== null && status.phase === 'pr_open' && (
        <div className="neo-change-action">
          {status.canMerge ? (
            merging ? (
              <MergeForm
                changeId={changeId}
                headSha={status.pr.headSha}
                onDone={() => {
                  setMerging(false);
                  reload();
                }}
              />
            ) : (
              <button
                type="button"
                className="admin-button-affirm"
                onClick={() => setMerging(true)}
              >
                Merge…
              </button>
            )
          ) : (
            <p className="admin-muted">Merge is unavailable: {status.mergeBlockedReason}</p>
          )}
        </div>
      )}
      {status.phase === 'failed' && (
        <p className="admin-muted">
          The coding agent stopped before opening a PR. Its reasoning is in the latest run&apos;s
          summary. Ask Neo to draft the change again with what it needs.
        </p>
      )}
      {status.behind && (
        <div className="neo-change-action">
          <button
            type="button"
            className="admin-button-secondary"
            disabled={busy !== null}
            onClick={() => void act('refresh')}
          >
            {busy === 'refresh' ? 'Refreshing…' : 'Refresh onto main'}
          </button>
          <span className="admin-muted">
            Re-applies the PR onto current main, so CI runs again.
          </span>
        </div>
      )}
      {status.pr !== null && status.phase === 'pr_open' && (
        <form
          className="neo-change-action neo-change-revise"
          onSubmit={(e) => {
            e.preventDefault();
            void act('revise', { request: revision.trim() });
          }}
        >
          <label htmlFor="neo-change-revision">Ask for a revision</label>
          <textarea
            id="neo-change-revision"
            value={revision}
            onChange={(e) => setRevision(e.target.value)}
            rows={3}
            maxLength={8000}
            placeholder="What to change in this PR. The agent also reads the PR's review comments and any failing CI."
          />
          <button
            type="submit"
            className="admin-button-secondary"
            disabled={busy !== null || revision.trim().length === 0}
          >
            {busy === 'revise' ? 'Starting…' : 'Revise'}
          </button>
        </form>
      )}
      <div className="neo-change-action">
        {confirmClose ? (
          <span className="admin-confirm">
            <span className="admin-confirm-prompt">Close the PR and drop this change?</span>
            <button
              type="button"
              className="admin-button-danger"
              disabled={busy !== null}
              onClick={() => void act('close')}
            >
              Close it
            </button>
            <button
              type="button"
              className="admin-link-button"
              onClick={() => setConfirmClose(false)}
            >
              Keep it
            </button>
          </span>
        ) : (
          <button type="button" className="admin-link-button" onClick={() => setConfirmClose(true)}>
            Close change…
          </button>
        )}
      </div>
      {error !== null && <p className="admin-inline-error">{error}</p>}
    </div>
  );
}

export function AdminNeoChangeDetail() {
  const { id = '' } = useParams();
  const [data, setData] = useState<NeoChangeDetail | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(() => {
    adminApi<NeoChangeDetail>(`/v1/admin/neo/changes/${id}`)
      .then((res) => {
        setData(res);
        setError(null);
      })
      .catch((err) => setError(apiErrorMessage(err, 'Failed to load the change.')));
  }, [id]);

  const phase = data?.status?.phase;
  useEffect(() => {
    load();
  }, [load]);
  // Keep a moving change fresh: the agent, CI and the deploy all take minutes.
  useEffect(() => {
    if (phase === undefined || !['running', 'pr_open', 'deploying'].includes(phase)) return;
    const timer = window.setInterval(load, POLL_MS);
    return () => window.clearInterval(timer);
  }, [phase, load]);

  if (data === null) {
    return (
      <section className="admin-section">
        <p>
          <Link to="/admin/neo/changes">← Product changes</Link>
        </p>
        {error === null ? (
          <p className="admin-loading">Loading…</p>
        ) : (
          <p className="admin-inline-error">{error}</p>
        )}
      </section>
    );
  }

  const { change, status } = data;
  const shown = status === null ? null : phaseLabel(status.phase);
  const ci = status === null ? null : ciLabel(status.ci);
  return (
    <section className="admin-section">
      <p>
        <Link to="/admin/neo/changes">← Product changes</Link>
      </p>
      <header className="admin-section-header">
        <h1>{change.title}</h1>
        {shown !== null && <StatusBadge status={shown.badge} label={shown.label} />}
      </header>
      {data.statusError !== null && (
        <div className="admin-auth-banner">Live status is unavailable: {data.statusError}</div>
      )}
      {error !== null && <p className="admin-inline-error">{error}</p>}

      <dl className="admin-defs">
        <dt>Asked by</dt>
        <dd>
          {change.requestedBy ?? '—'}, {formatDate(change.createdAt)}
        </dd>
        {status?.run != null && (
          <>
            <dt>Latest run</dt>
            <dd>
              <a href={status.run.url} target="_blank" rel="noreferrer">
                {status.run.status === 'completed' ? status.run.conclusion : status.run.status}
              </a>
            </dd>
          </>
        )}
        {status?.pr != null && (
          <>
            <dt>Pull request</dt>
            <dd>
              <a href={status.pr.url} target="_blank" rel="noreferrer">
                #{status.pr.number} {status.pr.title}
              </a>{' '}
              <span className="admin-muted">
                {status.pr.changedFiles} files, +{status.pr.additions} −{status.pr.deletions}
              </span>
            </dd>
            <dt>CI</dt>
            <dd>{ci !== null && <StatusBadge status={ci.badge} label={ci.label} />}</dd>
          </>
        )}
        {change.state === 'merged' && (
          <>
            <dt>Merged</dt>
            <dd>
              {change.mergedBy ?? '—'}, {formatDate(change.mergedAt)}
              {status?.deploy != null && (
                <>
                  {' · '}
                  <a href={status.deploy.url} target="_blank" rel="noreferrer">
                    deploy{' '}
                    {status.deploy.status === 'completed'
                      ? status.deploy.conclusion
                      : status.deploy.status}
                  </a>
                </>
              )}
            </dd>
          </>
        )}
      </dl>

      {status?.pr != null && status.pr.sensitiveFiles.length > 0 && (
        <div className="admin-auth-banner">
          <strong>Read these closely before merging:</strong>
          <ul className="neo-change-files">
            {status.pr.sensitiveFiles.map((f) => (
              <li key={f}>
                <code>{f}</code>
              </li>
            ))}
          </ul>
        </div>
      )}

      {status !== null && <Actions changeId={change.id} status={status} reload={load} />}

      {status?.pr != null && status.pr.files.length > 0 && (
        <details className="neo-change-filelist">
          <summary>All {status.pr.files.length} changed files</summary>
          <ul className="neo-change-files">
            {status.pr.files.map((f) => (
              <li key={f}>
                <code>{f}</code>
              </li>
            ))}
          </ul>
        </details>
      )}

      <h2>The request</h2>
      <div className="admin-info-card">
        <NeoMarkdown content={change.request} />
      </div>
    </section>
  );
}
